import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import { JiraTicket, ViewMode } from '../../types';

interface STSTicketPanelProps {
  tickets: JiraTicket[];
  loading: boolean;
  selectedTicket: JiraTicket | null;
  onTicketSelect: (ticket: JiraTicket) => void;
  onRefresh: () => void;
  viewMode: ViewMode;
  mobileMode?: boolean;
  tabletMode?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing(2, 2.5, 1.5),
    borderBottom: '1px solid #e0e0e0',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#000000',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  subtitle: {
    fontSize: '0.75rem',
    color: '#616161',
    marginTop: theme.spacing(0.25),
  },
  searchField: {
    marginBottom: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: 999,
      fontSize: '0.75rem',
      backgroundColor: '#f8fafc',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#94a3b8',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
    },
    '& .MuiInputBase-input': {
      padding: '4px 2px',
      fontSize: '0.72rem',
    },
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '2px 20px 20px',
    marginTop: theme.spacing(1.5),
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e2e8f0',
      borderRadius: 2,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#64748b',
  },
  card: {
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&:hover:not($selectedCard)': {
      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
      transform: 'translateY(-1px)',
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    '& $ticketKey': { color: '#ffffff' },
    '& $summary': { color: '#ffffff' },
    '& $tag': { backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff' },
    '& $priorityTag': { backgroundColor: 'rgba(255,255,255,0.2) !important', color: '#ffffff !important' },
    '& $footer': { borderTopColor: 'rgba(255,255,255,0.2)' },
    '& $statusLabel': { color: '#ffffff' },
    '& $statusDot': { opacity: 0.9 },
    '& $avatar': { backgroundColor: 'rgba(255,255,255,0.3)', color: '#ffffff' },
    '& $assignee': { color: '#ffffff' },
  },
  cardContent: {
    padding: '12px 14px !important',
  },
  ticketKey: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    letterSpacing: '0.3px',
  },
  summary: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.75),
    display: '-webkit-box' as any,
    overflow: 'hidden',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
  },
  tagsRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as any,
    marginBottom: theme.spacing(1),
  },
  tag: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#475569',
    backgroundColor: '#f1f5f9',
    padding: '2px 7px',
    borderRadius: 6,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as any,
  },
  priorityTag: {
    fontSize: '0.68rem',
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 6,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as any,
    border: '1px solid transparent',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    borderTop: '1px solid #f1f5f9',
    marginTop: theme.spacing(0.5),
  },
  assignee: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: '#64748b',
  },
  avatar: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontSize: '0.6rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#475569',
  },
}));

export const STSTicketPanel: React.FC<STSTicketPanelProps> = ({
  tickets,
  loading,
  selectedTicket,
  onTicketSelect,
  onRefresh,
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTickets = useMemo(() =>
    tickets.filter(ticket =>
      !searchQuery ||
      ticket.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase())
    )
  , [tickets, searchQuery]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerRow}>
          <Typography className={classes.title}>
            Tickets
          </Typography>
          <IconButton size="small" onClick={onRefresh} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box style={{ padding: '12px 20px 0' }}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Search..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 14, color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      <Box className={classes.content}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={36} />
          </Box>
        ) : filteredTickets.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No tickets found</Typography>
          </Box>
        ) : (
          filteredTickets.map(ticket => {
            const isSelected = selectedTicket?.id === ticket.id;
            return (
              <Card
                key={ticket.id}
                className={`${classes.card} ${isSelected ? classes.selectedCard : ''}`}
                onClick={() => !isSelected && onTicketSelect(ticket)}
                elevation={0}
              >
                <CardContent className={classes.cardContent}>
                  <Typography component="span" className={classes.ticketKey}>
                    {ticket.ticketNumber}
                  </Typography>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
};